import { useBoolean, When } from '@teamsparta/react';
import { Button } from '@teamsparta/stack-button';
import { Flex } from '@teamsparta/stack-flex';
import { ArrowChevronDown } from '@teamsparta/stack-icons';
import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';

import type { CommunityPostCommentSortBy } from '@/api/community/comment';
import { useIsDesktop } from '@/hooks';
import { Dialog, Popover } from '@/pages/community/shared';

interface Props {
  value: CommunityPostCommentSortBy;
  onChange?: (sortBy: CommunityPostCommentSortBy) => void;
}

const SORT_BY_OPTIONS = {
  liked: '추천순',
  created: '최신순',
};

export function SortBy({ value, onChange }: Props) {
  const isDesktop = useIsDesktop();

  const {
    value: dialogOpen,
    setTrue: openDialog,
    setFalse: closeDialog,
  } = useBoolean();
  const {
    value: popoverOpen,
    setTrue: openPopover,
    setFalse: closePopover,
  } = useBoolean();

  function handleChange(sortBy: CommunityPostCommentSortBy) {
    return () => {
      onChange?.(sortBy);
      closeDialog();
      closePopover();
    };
  }

  return (
    <When
      condition={isDesktop}
      fallback={
        <Dialog.Root
          open={dialogOpen}
          onOpenChange={(nextOpen) => (nextOpen ? openDialog() : closeDialog())}
        >
          <Dialog.Trigger>
            <Flex.Row align='center' gap={2}>
              <Text as='span' font='tag1Sb' color={vars.neutral[50]}>
                {SORT_BY_OPTIONS[value]}
              </Text>

              <ArrowChevronDown size={16} color={vars.neutral[50]} />
            </Flex.Row>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content style={{ padding: '24px 0' }}>
              <Flex.Column fullWidth gap={8}>
                <Text
                  as='button'
                  font='bodyM'
                  color={vars.neutral[0]}
                  style={{ padding: '13px 20px', width: '100%' }}
                  onClick={handleChange('created')}
                >
                  최신순
                </Text>
                <Text
                  as='button'
                  font='bodyM'
                  color={vars.neutral[0]}
                  style={{ padding: '13px 20px', width: '100%' }}
                  onClick={handleChange('liked')}
                >
                  추천순
                </Text>
              </Flex.Column>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      }
    >
      <Popover.Root
        open={popoverOpen}
        onOpenChange={(nextOpen) => (nextOpen ? openPopover() : closePopover())}
      >
        <Popover.Trigger>
          <Flex.Row align='center' gap={2}>
            <Text as='span' font='tag1Sb' color={vars.neutral[50]}>
              {SORT_BY_OPTIONS[value]}
            </Text>

            <ArrowChevronDown size={16} color={vars.neutral[50]} />
          </Flex.Row>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align='end'>
            <Flex.Column fullWidth>
              <Button
                size='xs'
                colorScheme='tertiary'
                onClick={handleChange('created')}
              >
                최신순
              </Button>
              <Button
                size='xs'
                colorScheme='tertiary'
                onClick={handleChange('liked')}
              >
                추천순
              </Button>
            </Flex.Column>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </When>
  );
}
